import { defaultFirebaseAnalytics } from '@/utils/firebase'
import { getVliveId } from '@/utils/vlive_id'
import { getToken } from '@/utils/token'
import { isProduction } from '@/utils/env'
import {
  Analytics,
  CustomParams,
  Item,
  logEvent,
  setUserId,
  setUserProperties,
} from 'firebase/analytics'

export enum UserPropertyKey {
  vliveId = 'vlive_id',
}

export interface AnalyticsEvent {
  name: string
  params: CustomParams | {}
}
export interface AnalyticsPurchaseEventParams {
  transaction_id: string
  currency: string
  value: number
  payment_type: string
  items: [Item]
  purchase_id: string
}

export class EventTracker {
  constructor(private readonly firebaseAnalytics: Analytics) {}
  public static readonly default = new EventTracker(defaultFirebaseAnalytics)
  private sessionToken: string | undefined = undefined

  async setup() {
    // セッショントークンを保持している場合はvliveIdを取得しておく
    // vliveIdを取得後にセッションを更新する
    const sessionToken = getToken()
    if (sessionToken === undefined) return

    const vliveId = await getVliveId()
    if (vliveId !== undefined) {
      this.setVliveId(vliveId)
      this.sessionToken = sessionToken
    }
  }

  public trackPurchase(
    productId: string,
    productName: string,
    currency: string,
    price: number,
    transactionId: string,
    paymentMethodType: string,
    purchaseId: string
  ) {
    const item: Item = {
      item_id: productId,
      item_name: productName,
    }
    const params: AnalyticsPurchaseEventParams = {
      transaction_id: transactionId,
      currency: currency,
      value: price,
      payment_type: paymentMethodType,
      items: [item],
      purchase_id: purchaseId,
    }
    const event: AnalyticsEvent = { name: 'purchase', params }
    this.logEvent(event)
  }

  public trackPageLcp(
    pageLcp: number,
    pathname: string,
    contentUrl: string,
    contentWidth: number,
    contentHeight: number
  ) {
    const params: object = {
      page_lcp: pageLcp,
      pathname: pathname,
      content_url: contentUrl,
      content_width: contentWidth,
      content_height: contentHeight,
    }
    this.logEvent({ name: 'web_page_lcp', params })
  }

  public trackPageFcp(pageFcp: number, pathname: string) {
    const params: object = {
      page_fcp: pageFcp,
      pathname: pathname,
    }
    this.logEvent({ name: 'web_page_fcp', params })
  }

  public trackTapEventEntryButton(eventId: number | string) {
    const params: object = {
      content_type: 'join',
      event_id: eventId.toString(),
    }
    this.logEvent({ name: 'select_content', params })
  }

  public trackSelectGroupEventButton(
    eventId: number | string,
    groupId: number | string
  ) {
    const params: object = {
      firebase_screen: 'EventScorePage',
      content_type: 'groupevent_score',
      original_id: eventId.toString(),
      group_id: groupId.toString(),
    }
    this.logEvent({ name: 'select_content', params })
  }

  public trackSelectPremiumButton() {
    const params: object = {
      firebase_screen: 'WebCoinShop',
      content_type: 'web_premium_entry_button',
    }
    this.logEvent({ name: 'select_content', params })
  }

  public trackSelectMembershipButton() {
    const params: object = {
      firebase_screen: 'WebCoinShop',
      content_type: 'web_membership_entry_button',
    }
    this.logEvent({ name: 'select_content', params })
  }

  public setVliveId(vliveId: string) {
    this.setUserProperties(UserPropertyKey.vliveId, vliveId)
    setUserId(this.firebaseAnalytics, vliveId)

    if (!isProduction()) {
      // eslint-disable-next-line
      console.log('set vliveId:', vliveId)
    }
  }

  private logEvent(event: AnalyticsEvent) {
    const log = (
      firebaseAnalytics: Analytics,
      name: string,
      params: CustomParams
    ) => {
      logEvent(firebaseAnalytics, name, params)
      if (!isProduction()) {
        // eslint-disable-next-line
        console.log('log event:', name, params)
      }
    }

    // セッショントークンが変わった場合はvliveIdを再取得しておく
    // vliveIdを取得後にセッションを更新する
    const sessionToken = getToken()
    if (sessionToken !== this.sessionToken) {
      getVliveId()
        .then((vliveId) => {
          if (vliveId !== undefined) {
            this.setVliveId(vliveId)
            this.sessionToken = sessionToken
          }
        })
        .finally(() => {
          log(this.firebaseAnalytics, event.name, event.params)
        })
    } else {
      log(this.firebaseAnalytics, event.name, event.params)
    }
  }

  private setUserProperties(key: UserPropertyKey, value: string) {
    const properties: CustomParams = {}
    properties[key] = value
    setUserProperties(this.firebaseAnalytics, properties)
  }
}
